import { IapProduct } from 'models'
import { throwError, of } from 'rxjs'
import VoltError from 'VoltError'
import Fetch from '../fetch'

/**
 * Deal with Purchase (data retrieval, purchase and subscription actions)
 */
export default class PurchaseApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.gatewayApi = otherApis.gatewayApi
    }

    /**
     * Request to purchase a TVOD product
     * @param {String} productId
     * @param {String} offerId
     * @param {PurchaseData} purchaseData
     * @returns {Observable<Boolean>} returns true if the purchase was successful
     */
    purchaseTVOD(productId, offerId, purchaseData = {}) {
        return this.gatewayApi.purchaseTVOD(productId, offerId, purchaseData)
    }

    /**
     * Request to purchase a subscription product
     * @param {String} productId
     * @param {String} offerId
     * @param {PurchaseData} purchaseData
     * @returns {Observable<Boolean>} returns true if the purchase subscription was successful`
     */
    subscribe(productId, offerId, purchaseData = {}) {
        return this.gatewayApi.subscribe(productId, offerId, purchaseData)
    }

    /**
     * Request to purchase a subscription product
     *
     * @param {String} productId
     * @param {String} offerId
     * @param {PurchaseData} purchaseData
     * @param {String} [iapReceipt] (Optionally) iapReceipt is used to validate app store purchase. MarketONE has decide to reuse the purchase API to simplify the design
     * @param {String} [iapAppstore] (Optionally) iapAppStore is used to indicate which store is used (GOOGLE, APPLE, AMAZON)
     *
     * @returns {Observable}
     */
    validateAppStorePurchase(productId, offerId, purchaseData, iapReceipt, iapAppstore) {
        if (!iapAppstore !== IapProduct.IAP_APP_STORE.APPLE) {
            this.logger.warn('This method supposed to be called only for Apple Store')
            return of(false)
        }

        return this.gatewayApi.notifyPurchaseEntitlement({
            receipt: iapReceipt,
            productId,
            purchaseData,
        })
    }

    /**
     *
     * @typedef {Object} PurchaseData
     * @property {Object} profile
     * @property {String} currency
     * @property {String} price
     * @property {String} title
     * @property {String} affiliateId
     * @property {String} contentId
     * @property {String} paymentMethod
     * @property {String} description
     * @property {String} provider
     * @property {String} offerType
     */

    // --------------------- FEATURE NOT SUPPORTED ---------------------
    activateSubscription({ productId, activationId }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[activateSubscription] Feature not supported yet',
            })
        )
    }

    getPurchasedTVODProducts({ limit, page }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getPurchasedTVODProducts] Feature not supported yet',
            })
        )
    }

    getAllPurchasedTVODProducts(limit = 15) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getAllPurchasedTVODProducts] Feature not supported yet',
            })
        )
    }

    getSubscriptions(pageSize = 15) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getSubscriptions] Feature not supported yet',
            })
        )
    }

    getProductsFromTitle({ id: titleId }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getProductsFromTitle] Feature not supported yet',
            })
        )
    }

    getTVODProductsData(productIds) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getTVODProductsData] Feature not supported yet',
            })
        )
    }

    getSubscriptionsData(productIds) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getSubscriptionsData] Feature not supported yet',
            })
        )
    }

    unsubscribe(productId, offerId, transactionId) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[unsubscribe] Unsubscribe should be done on the Web APP',
            })
        )
    }

    /**
     * Redeem a voucher code
     *
     * @param {Object} options
     * @param {String} options.code value of voucherCode
     * @returns {Observable<Boolean>} returns boolean
     */
    redeemVoucherCode = ({ code }) => {
        return of(false)
    }

    /**
     * Add payment method for an account
     *
     * @param {Object} options
     * @param {string} options.paymentMethodId the payment method id got from a credit card iframe
     * @returns Observable
     */
    addPaymentMethod = ({ paymentMethodId }) =>
        throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))

    /**
     * Delete payment method for an account
     *
     * @param {Object} options
     * @param {string} options.paymentMethodId the payment method id got from a credit card iframe
     * @returns Observable
     */
    deletePaymentMethod = ({ paymentMethodId }) =>
        throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))

    sendPurchaseOTP = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    validatePurchaseOTP = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    getUserWallet = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    // ------------------------------------------------------------------------
}
